import React, { useEffect, useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, ClipboardList, Images, Router, Users, Tag,
  RefreshCw, Bot, Settings, LogOut, ChevronLeft, ChevronRight, Satellite,
  Gauge, ListChecks, Contact, MessagesSquare, KeyRound, Wallet, HandCoins, PiggyBank,
  Wifi, Receipt, History, Cable, Sparkles, GitBranch, Ticket, Layers, Menu, X, Server, Activity, Map, Gift
} from 'lucide-react'
import { getRole, getNama, clearAuthData } from '../auth'
import useBranding from '../useBranding'

// `roles` menentukan siapa yg lihat item ini di sidebar -- default cuma admin kalau tidak
// disebut. Tiket Gangguan/List Pekerjaan/Pengaturan sengaja dibuka utk teknisi juga (Pengaturan
// sendiri bercabang isinya by role, lihat Pengaturan.jsx -- teknisi cuma lihat kartu Ganti Password).
//
// `section` (2026-07-22, audit) -- murni label visual pengelompokan di sidebar, TIDAK mempengaruhi
// routing/akses. Header section dirender otomatis di antara grup yg beda, cuma muncul kalau ada
// minimal 1 item di grup itu yg visible utk role user (lihat renderMenu di bawah).
//
// Script/Driver OLT/Target Kerja SENGAJA TIDAK ada di sini lagi (2026-07-24, audit) -- masing2
// halaman kecil/nyaris redundan (Script cuma versi manual dari tombol Sync yg sudah ada di
// Perangkat Pelanggan, Driver OLT katalog read-only 2 baris, Target Kerja cuma 1 angka), routenya
// TETAP ada & jalan seperti biasa, cuma dipindah jadi link cepat di "Peta Pengaturan" (lihat
// Pengaturan.jsx) supaya tidak menyita slot menu utama.
const menu = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin'], section: 'Operasional' },
  { path: '/rekap', icon: ClipboardList, label: 'Rekap Pekerjaan', roles: ['admin'], section: 'Operasional' },
  { path: '/foto', icon: Images, label: 'Bukti Foto', roles: ['admin'], section: 'Operasional' },
  { path: '/perangkat', icon: Router, label: 'Perangkat Pelanggan', roles: ['admin'], section: 'Operasional' },
  { path: '/tiket-gangguan', icon: Gauge, label: 'Tiket Gangguan', roles: ['admin', 'teknisi'], section: 'Operasional' },
  { path: '/list-pekerjaan', icon: ListChecks, label: 'List Pekerjaan', roles: ['admin', 'teknisi'], section: 'Operasional' },
  { path: '/pelanggan-kontak', icon: Contact, label: 'Daftar Pelanggan', roles: ['admin', 'teknisi'], section: 'Operasional' },
  { path: '/peta-pelanggan', icon: Map, label: 'Peta Pelanggan', roles: ['admin', 'teknisi'], section: 'Operasional' },
  { path: '/log-chat-ai', icon: MessagesSquare, label: 'Log Chat AI', roles: ['admin'], section: 'Operasional' },
  { path: '/model-ont', icon: KeyRound, label: 'Model ONT', roles: ['admin'], section: 'Operasional' },
  { path: '/perangkat-olt', icon: Satellite, label: 'Perangkat OLT', roles: ['admin'], section: 'Operasional' },
  { path: '/pegawai', icon: Users, label: 'Pegawai', roles: ['admin'], section: 'SDM & Payroll' },
  { path: '/payroll', icon: Wallet, label: 'Payroll', roles: ['admin'], section: 'SDM & Payroll' },
  { path: '/kasbon', icon: HandCoins, label: 'Kasbon', roles: ['admin'], section: 'SDM & Payroll' },
  { path: '/tabungan', icon: PiggyBank, label: 'Tabungan', roles: ['admin'], section: 'SDM & Payroll' },
  { path: '/jenis-pekerjaan', icon: Tag, label: 'Jenis Pekerjaan', roles: ['admin'], section: 'SDM & Payroll' },
  { path: '/paket-langganan', icon: Wifi, label: 'Paket Langganan', roles: ['admin'], section: 'Billing' },
  { path: '/tagihan', icon: Receipt, label: 'Tagihan', roles: ['admin', 'teknisi'], section: 'Billing' },
  { path: '/history-pembayaran', icon: History, label: 'History Pembayaran', roles: ['admin', 'teknisi'], section: 'Billing' },
  { path: '/bonus-pelanggan', icon: Gift, label: 'Bonus Pelanggan', roles: ['admin'], section: 'Billing' },
  { path: '/pengaturan-mikrotik', icon: Cable, label: 'Koneksi Mikrotik', roles: ['admin'], section: 'Billing' },
  { path: '/paket-voucher', icon: Layers, label: 'Paket Voucher', roles: ['admin'], section: 'Billing' },
  { path: '/voucher', icon: Ticket, label: 'Voucher', roles: ['admin'], section: 'Billing' },
  { path: '/sync', icon: RefreshCw, label: 'Sync & Log', roles: ['admin'], section: 'Sistem' },
  { path: '/bot', icon: Bot, label: 'Pengaturan Bot', roles: ['admin'], section: 'Sistem' },
  { path: '/ai-asisten', icon: Sparkles, label: 'AI Asisten', roles: ['admin'], section: 'Sistem' },
  { path: '/ai-platforms', icon: Server, label: 'Platform AI', roles: ['admin'], section: 'Sistem' },
  { path: '/log-fix', icon: GitBranch, label: 'Log Fix', roles: ['admin'], section: 'Sistem' },
  { path: '/service', icon: Activity, label: 'Service', roles: ['admin'], section: 'Sistem' },
  { path: '/pengaturan', icon: Settings, label: 'Pengaturan', roles: ['admin', 'teknisi'], section: 'Sistem' },
]

export default function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  // Drawer mobile selalu tampil penuh (icon+label) meski toggle "collapsed" desktop lagi aktif --
  // collapsed cuma masuk akal utk sidebar permanen, drawer overlay yg cuma muncul sebentar harus
  // tetap kebaca labelnya.
  const effectiveCollapsed = collapsed && !mobileOpen
  const role = getRole()
  const nama = getNama()
  const businessName = useBranding()
  const menuTampil = menu.filter(m => m.roles.includes(role))

  // Judul tab browser ikut Nama Usaha (2026-07-22, audit portabilitas/white-label) -- sebelumnya
  // "Teknisi Rekap — FazNet" statis di public/index.html, sekarang di-set dari sini begitu
  // businessName selesai dimuat (index.html tetap punya title generik sbg fallback SEBELUM JS jalan).
  useEffect(() => { document.title = `${businessName} — Admin` }, [businessName])

  // Drawer mobile auto-close tiap pindah halaman (2026-07-26) -- tanpa ini, sidebar overlay
  // tetap terbuka nutupin konten setelah user tap menu, harus tap overlay manual tiap kali.
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  function logout() {
    clearAuthData()
    navigate('/login')
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <div className={`app-overlay${mobileOpen ? ' open' : ''}`} onClick={() => setMobileOpen(false)} />
      {/* Sidebar */}
      <aside className={`app-sidebar${mobileOpen ? ' open' : ''}`} style={{
        width: effectiveCollapsed ? 68 : 236,
        background: '#101018',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.18s ease',
        flexShrink: 0,
        borderRight: '1px solid rgba(255,255,255,0.06)',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ padding: '18px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 10, minHeight: 60 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            background: 'linear-gradient(135deg, var(--brand), #7c8cf8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Satellite size={16} color="#fff" />
          </div>
          {!effectiveCollapsed && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', letterSpacing: -0.2 }}>{businessName}</div>
              <div style={{ fontSize: 10.5, color: '#8a8a99' }}>Rekap Teknisi</div>
            </div>
          )}
          <button className="app-collapse-btn" onClick={() => setCollapsed(!collapsed)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#8a8a99', cursor: 'pointer', display: 'flex', padding: 4 }}>
            {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>
          <button className="app-close-btn" onClick={() => setMobileOpen(false)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#8a8a99', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Menu */}
        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {menuTampil.map((m, i) => (
            <React.Fragment key={m.path}>
              {m.section !== menuTampil[i - 1]?.section && (
                effectiveCollapsed
                  ? <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 6px' }} />
                  : <div style={{ padding: '14px 12px 6px', fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#5c5c6b' }}>{m.section}</div>
              )}
              <NavLink to={m.path}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 11,
                  padding: '9px 12px',
                  color: isActive ? '#fff' : '#9797a5',
                  background: isActive ? 'var(--brand)' : 'transparent',
                  borderRadius: 9,
                  textDecoration: 'none', fontSize: 13, fontWeight: isActive ? 600 : 500,
                  transition: 'background 0.12s, color 0.12s'
                })}>
                <m.icon size={17} strokeWidth={2} style={{ flexShrink: 0 }} />
                {!effectiveCollapsed && <span>{m.label}</span>}
              </NavLink>
            </React.Fragment>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ padding: 10, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {!effectiveCollapsed && nama && (
            <div style={{ padding: '4px 12px 10px' }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nama}</div>
              <div style={{ fontSize: 10.5, color: '#8a8a99', textTransform: 'capitalize' }}>{role}</div>
            </div>
          )}
          <button onClick={logout} style={{
            width: '100%', padding: '9px 12px', background: 'transparent',
            border: 'none', borderRadius: 9, color: '#9797a5',
            cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 11,
            transition: 'background 0.12s, color 0.12s'
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.12)'; e.currentTarget.style.color = '#f87171' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9797a5' }}
          >
            <LogOut size={17} />{!effectiveCollapsed && 'Logout'}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div className="app-topbar">
          <button className="app-hamburger" onClick={() => setMobileOpen(true)} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', padding: 4
          }}>
            <Menu size={22} />
          </button>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{businessName}</div>
        </div>
        <main className="app-main" style={{ flex: 1, overflow: 'auto', padding: 28, maxWidth: '100%' }}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
